import PageHeader from "../components/PageHeader";
import EditorialMarquee from "../components/EditorialMarquee";
import CTASection from "../components/CTASection";
import { Reveal, RevealWordsOnScroll } from "../components/Reveal";
import { IMAGES } from "../data/site";
import { Flower2, Sparkles, Lightbulb, Armchair, Palette, LayoutTemplate } from "lucide-react";

const STYLES = [
  { icon: Flower2, t: "Floral Design", d: "Lush centerpieces, aisle florals and statement installations arranged by our in-house designers." },
  { icon: LayoutTemplate, t: "Stage & Backdrops", d: "Custom mandap, mansnup and reception stages built and styled to your theme." },
  { icon: Lightbulb, t: "Lighting & Ambiance", d: "Uplighting, chandeliers and pin-spots that transform the hall from ceremony to celebration." },
  { icon: Armchair, t: "Draping & Linens", d: "Ceiling drapes, chair treatments and premium linens in your palette." },
  { icon: Palette, t: "Color & Theme", d: "From classic ivory & gold to bold cultural palettes — styled cohesively throughout." },
  { icon: Sparkles, t: "Finishing Touches", d: "Table numbers, signage, charger plates and the little details that tie it together." },
];

export default function Decor() {
  return (
    <div data-testid="decor-page">
      <PageHeader
        overline="In-House Decoration"
        titleLines={["Décor styled", <span key="i" className="italic text-gold">to your vision</span>]}
        subtitle="No outside decorator needed. Our in-house team designs and installs everything — from our signature red rose installations to blush and white florals, draping, lighting and stages — so the hall feels effortlessly, unmistakably yours."
        image={IMAGES.decorHero}
        imageAlt="Vista Banquet Hall signature red rose décor and floral styling"
        testId="decor-page-header"
      />

      {/* service styles */}
      <section className="mx-auto max-w-[1400px] px-5 md:px-10 pb-8 md:pb-16">
        <div className="mb-14 max-w-2xl">
          <span className="overline text-gold">What We Style</span>
          <h2 className="mt-6 font-serif text-4xl md:text-5xl font-light leading-[1.05] tracking-tight text-navy">
            <RevealWordsOnScroll text="Every detail, designed under one roof." />
          </h2>
        </div>
        <div className="grid gap-px bg-gold/25 border border-gold/25 sm:grid-cols-2 lg:grid-cols-3">
          {STYLES.map((s, i) => (
            <Reveal key={s.t} delay={(i % 3) * 0.08} className="bg-ivory p-8 md:p-10">
              <s.icon size={26} className="text-gold" />
              <h3 className="mt-5 font-serif text-2xl md:text-3xl font-light text-navy">{s.t}</h3>
              <p className="mt-3 text-navy/60 font-light leading-relaxed">{s.d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <EditorialMarquee items={["Floral Design", "Custom Stages", "Lighting", "Draping & Linens"]} />

      {/* image editorial */}
      <section className="bg-navy text-ivory">
        <div className="mx-auto grid max-w-[1400px] gap-14 px-5 md:grid-cols-12 md:px-10 py-24 md:py-32">
          <div className="md:col-span-5">
            <span className="overline text-gold">The Vista Look</span>
            <h2 className="mt-6 font-serif text-4xl md:text-5xl font-light leading-[1.05] tracking-tight">
              A room transformed for your day.
            </h2>
            <Reveal delay={0.15}>
              <p className="mt-6 text-ivory/65 font-light leading-relaxed">
                We begin with your inspiration and end with a hall that photographs beautifully
                from every angle — coordinated florals, lighting and staging that feel considered,
                never templated.
              </p>
            </Reveal>
          </div>
          <div className="md:col-span-7 md:pl-8">
            <div className="grid grid-cols-2 gap-4">
              <Reveal className="overflow-hidden rounded-sm border border-ivory/15 aspect-[3/4]">
                <img src={IMAGES.decorCandles} alt="Candlelit décor" className="h-full w-full object-cover" />
              </Reveal>
              <Reveal delay={0.1} className="overflow-hidden rounded-sm border border-ivory/15 aspect-[3/4] mt-8">
                <img src={IMAGES.floralArch} alt="Floral arch styling" className="h-full w-full object-cover" />
              </Reveal>
              <Reveal delay={0.15} className="overflow-hidden rounded-sm border border-ivory/15 aspect-[3/4] -mt-8">
                <img src={IMAGES.rosesBouquet} alt="Floral centerpiece" className="h-full w-full object-cover" />
              </Reveal>
              <Reveal delay={0.2} className="overflow-hidden rounded-sm border border-ivory/15 aspect-[3/4]">
                <img src={IMAGES.decorTable} alt="Styled tablescape" className="h-full w-full object-cover" />
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <CTASection
        dark={false}
        overline="Let's Design Together"
        heading="Style your celebration with our team."
        body="Book a tour and walk through décor concepts with our in-house designers."
        actions={[{ to: "/tour", label: "Schedule a Tour", primary: true }, { to: "/gallery", label: "View the Gallery" }]}
        testId="cta-decor-page"
      />
    </div>
  );
}
